import { schema, supabase } from "../../lib/supabase";
import type { WorkContext } from "../../types";
export type Scope = { frente_id: string | null; subfrente_id: string | null };
export type Area = { id: string; area: string; descripcion?: string | null; default_area_id?: string | null };
export type DefaultArea = { id: string; nombre: string };
export type Frente = { id: string; frente: string; is_active: boolean | null };
export type Subfrente = { id: string; frente_id: string; subfrente: string; is_active: boolean | null };
export type Assignment = { id: string; area_id: string | null; rol: string; frente_id: string | null; subfrente_id: string | null; is_active: boolean };
export type ContractUser = { user_id: string; nombre: string; correo: string | null; asignaciones: Assignment[]; invitacion_pendiente?: boolean };
export type ContractCatalog = { admin: boolean; coordinador_areas: string[]; areas: Area[]; predefinidas: DefaultArea[]; frentes: Frente[]; subfrentes: Subfrente[]; usuarios: ContractUser[] };
export type UserOption = { user_id: string; nombre: string; correo: string | null };
export async function contractApi<T>(context: WorkContext, operation: string, payload: Record<string, unknown> = {}): Promise<T> {
  const { data, error } = await schema("obra").rpc("api_administracion_contrato" as never, {
    p_operacion: operation, p_proyecto_id: context.proyecto_id,
    p_contrato_id: context.contrato_id, p_payload: payload,
  } as never);
  if (error) throw new Error(error.message);
  return data as T;
}
/** Validar el JWT con Auth antes de ejecutar operaciones administrativas sensibles. */
async function requireCurrentAccessToken(): Promise<string> {
  const { data: { session }, error: sessionError } = await supabase.auth.getSession();
  if (sessionError && (!sessionError.status || sessionError.status >= 500)) {
    throw new Error("No fue posible consultar Supabase Auth. Comprueba la conexión antes de volver a intentar.");
  }
  if (sessionError || !session) {
    await returnToLogin();
    throw new Error("Tu sesión ya no está activa. Inicia sesión nuevamente.");
  }

  const verify = async (accessToken: string) => {
    const result = await supabase.auth.getUser(accessToken);
    if (!result.error && result.data.user?.id === session.user.id) return { valid: true, error: null };
    return { valid: false, error: result.error };
  };

  const first = await verify(session.access_token);
  if (first.valid) return session.access_token;

  // Una sesión invalidada puede permanecer en localStorage aunque la UI siga
  // mostrándola activa. Intentar refrescar una sola vez, sin ignorar errores.
  const code = first.error?.code;
  const status = first.error?.status;
  if (first.error && (!status || status >= 500 ||
    (code && !["session_not_found","session_expired","invalid_token","bad_jwt","jwt_expired"].includes(code)
      && status !== 401 && status !== 403))) {
    throw new Error("No fue posible comprobar la sesión con Supabase Auth. Verifica tu conexión e inténtalo de nuevo.");
  }
  const { data: refreshed, error: refreshError } = await supabase.auth.refreshSession();
  if (refreshError && (!refreshError.status || refreshError.status >= 500)) {
    throw new Error("Supabase Auth no respondió al intentar renovar la sesión. Inténtalo nuevamente.");
  }
  if (!refreshError && refreshed.session?.access_token &&
      refreshed.session.user.id === session.user.id) {
    const second = await verify(refreshed.session.access_token);
    if (second.valid) return refreshed.session.access_token;
    if (second.error && second.error.status !== 401 && second.error.status !== 403) {
      throw new Error("No fue posible comprobar la sesión renovada. Inténtalo de nuevo.");
    }
  }
  await returnToLogin();
  throw new Error("La sesión fue revocada o expiró. Vuelve a iniciar sesión.");
}

async function returnToLogin(): Promise<void> {
  // Cerrar únicamente esta sesión local, nunca las de otros dispositivos.
  await supabase.auth.signOut({ scope: "local" }).catch(() => null);
  if (typeof window !== "undefined") {
    window.location.assign("/acceso?sesion=expirada");
  }
}
async function invokeAuth<T>(name: string, body: Record<string, unknown>): Promise<T> {
  const accessToken = await requireCurrentAccessToken();
  const { data, error } = await supabase.functions.invoke(name, {
    body, headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (error) {
    if (typeof error === "object" && "context" in error) {
      const context = (error as { context?: unknown }).context;
      if (context instanceof Response) {
        const details = await context.clone().json().catch(() => null) as { error?: string; bloqueos?: string[] } | null;
        if (details?.error) {
          if (context.status === 401 && /sesión|session/i.test(details.error)) {
            await returnToLogin();
            throw new Error("La sesión ha dejado de estar vigente. Inicia sesión nuevamente.");
          }
          throw new Error(details.error + (details.bloqueos?.length ? ` Tablas: ${details.bloqueos.join(", ")}.` : ""));
        }
      }
    }
    if (error.name === "FunctionsFetchError") {
      throw new Error("No fue posible conectar con la función de Supabase. Verifica tu conexión y que la función desplegada permita las cabeceras CORS authorization, x-client-info, apikey y content-type.");
    }
    throw new Error(error.message);
  }
  if (data?.error) throw new Error(String(data.error));
  return data as T;
}
export async function deleteDatabaseUser(context: WorkContext, userId: string) {
  return invokeAuth<{ ok: true }>("eliminar-usuario-global", {
    proyecto_id: context.proyecto_id, contrato_id: context.contrato_id, user_id: userId,
  });
}
export async function inviteContractUser(context: WorkContext, values: { email: string; nombre: string }) {
  return invokeAuth<{ user_id: string }>("invitar-usuario-contrato", {
    proyecto_id: context.proyecto_id, contrato_id: context.contrato_id, ...values,
  });
}

export type UserPage = { usuarios: UserOption[]; total: number; pagina: number; tamano: number };
export type PersonalProfile = { nombre: string; apellido_paterno: string; apellido_materno: string; telefono: string; notas: string };
export async function usersAdminApi<T>(context: WorkContext, op: string, payload: Record<string, unknown> = {}): Promise<T> {
  const { data, error } = await schema("obra").rpc("api_usuarios_contrato_aux" as never, {
    p_operacion: op, p_proyecto_id: context.proyecto_id, p_contrato_id: context.contrato_id, p_payload: payload,
  } as never);
  if (error) throw new Error(error.message);
  return data as T;
}
